import {fieldHasValue, hasMeaningfulValue} from "./formTreeUtils";

export function normalizePrefillLabel(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/[^\w\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function extractNodeText(node) {
  if (typeof node === "string") return node;
  if (!node || typeof node !== "object") return "";
  if (Array.isArray(node)) {
    return node.map(extractNodeText).join(" ").trim();
  }
  return extractNodeText(node.children);
}

function getFieldLabelText(fieldNode) {
  if (
    !fieldNode ||
    !Array.isArray(fieldNode.children) ||
    fieldNode.children.length === 0
  ) {
    return "";
  }
  return extractNodeText(fieldNode.children[0]);
}

function isPaymentCardLabel(labelText) {
  const label = normalizePrefillLabel(labelText);
  if (!label || label.includes("health card")) return false;

  const compact = label.replace(/\s+/g, "");
  if (["cvc", "cvv"].includes(compact)) return true;

  return [
    "card number",
    "credit card",
    "expiration date",
    "expiry date",
    "exp date",
    "security code",
    "name on card",
    "cardholder",
    "payment method",
  ].some((term) => label.includes(term));
}

function isPaymentCardSection(sectionName) {
  const section = normalizePrefillLabel(sectionName);
  if (!section || section.includes("health card")) return false;

  return [
    "credit card",
    "card information",
    "payment information",
    "payment method",
    "billing card",
  ].some((term) => section.includes(term));
}

function isSensitivePaymentField(fieldNode, sectionName = "") {
  if (!fieldNode || typeof fieldNode !== "object") return false;
  if (fieldNode.key === "payment-method") return true;

  const labelText = getFieldLabelText(fieldNode);
  if (isPaymentCardLabel(labelText)) return true;

  return isPaymentCardSection(sectionName) && isPaymentCardLabel(labelText);
}

function sanitizeSavedPaymentMethod(value) {
  if (!value || typeof value !== "object" || value.saved !== true) return "";
  return {
    saved: true,
    last4: value.last4 ? String(value.last4).slice(-4) : "",
    brand: value.brand ? String(value.brand) : "",
  };
}

function clearSensitivePaymentValues(node) {
  if (!node || typeof node !== "object") return;

  if (node.dbData) {
    delete node.dbData;
  }

  const props = node.props || {};
  if (node.element === "input") {
    if (props.type === "checkbox" || props.type === "radio") {
      delete props.checked;
    } else if (props.type === "file") {
      delete props.fileFields;
    } else {
      delete props.value;
      delete props.defaultValue;
    }
  } else if (node.element === "textarea" || node.element === "select") {
    delete props.value;
    delete props.defaultValue;
  }

  if (Array.isArray(node.children)) {
    node.children.forEach(clearSensitivePaymentValues);
  }
}

function sanitizePaymentField(fieldNode, sectionName = "") {
  if (!isSensitivePaymentField(fieldNode, sectionName)) return;

  if (fieldNode.key === "payment-method") {
    const savedChild = fieldNode.children?.[1];
    if (savedChild && typeof savedChild === "object") {
      savedChild.children = sanitizeSavedPaymentMethod(savedChild.children);
    }
    return;
  }

  clearSensitivePaymentValues(fieldNode);
}

export function formatPrefillDate(value) {
  if (!value) return "";
  const rawValue = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(rawValue)) {
    return rawValue.slice(0, 10);
  }
  const parsedDate = new Date(rawValue);
  if (Number.isNaN(parsedDate.getTime())) {
    return rawValue;
  }
  return parsedDate.toISOString().slice(0, 10);
}

function getNestedValue(source, path) {
  if (!source || !path) return "";
  return String(path)
    .split(".")
    .reduce(
      (value, key) => (value && value[key] !== undefined ? value[key] : ""),
      source
    );
}

function calculateAgeFromDate(value) {
  const dob = formatPrefillDate(value);
  if (!dob) return "";

  const birthDate = new Date(`${dob}T00:00:00`);
  if (Number.isNaN(birthDate.getTime())) return "";

  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const hasBirthdayPassed =
    today.getMonth() > birthDate.getMonth() ||
    (today.getMonth() === birthDate.getMonth() &&
      today.getDate() >= birthDate.getDate());

  if (!hasBirthdayPassed) age -= 1;
  return age >= 0 ? String(age) : "";
}

function getDatePart(value, part) {
  const dob = formatPrefillDate(value);
  if (!dob) return "";
  const [year, month, day] = dob.split("-");
  if (part === "year") return year || "";
  if (part === "month") return month || "";
  if (part === "day") return day || "";
  return "";
}

export function buildPatientPrefillValues(patient, fallbackPid) {
  if (!patient) return {};

  const fullName = [patient.fName, patient.lName]
    .filter(Boolean)
    .join(" ")
    .trim();
  const pid = patient.pid || fallbackPid || "";
  const fileNumber = patient.fileNumber || "";
  const patientIdentifier = fileNumber || pid;
  const dob = formatPrefillDate(
    patient.dob || patient.dateOfBirth || patient.birthDate
  );
  const age = patient.age || patient.patientAge || calculateAgeFromDate(dob);

  return {
    fullName,
    fName: patient.fName || "",
    lName: patient.lName || "",
    mName: patient.mName || "",
    preferredName: patient.preferredName || "",
    email: patient.email || "",
    phone: patient.phone || "",
    homePhone: patient.homePhone || "",
    alternativePhone: patient.alternativePhone || "",
    dob,
    dateOfBirth: dob,
    birthDate: dob,
    birthYear: getDatePart(dob, "year"),
    birthMonth: getDatePart(dob, "month"),
    birthDay: getDatePart(dob, "day"),
    age,
    patientAge: age,
    gender: patient.gender || "",
    preferredPronoun: patient.preferredPronoun || "",
    referralVia: patient.referralVia || "",
    address1: patient.address1 || "",
    address2: patient.address2 || "",
    city: patient.city || "",
    province: patient.province || patient.state || "",
    postalCode: patient.postalCode || patient.zipCode || "",
    country: patient.country || "",
    emergencyContactName: patient.emergencyContactName || "",
    emergencyContactEmail: patient.emergencyContactEmail || "",
    emergencyContactPhone: patient.emergencyContactPhone || "",
    emergencyContactRelation: patient.emergencyContactRelation || "",
    height: patient.height || "",
    weight: patient.weight || "",
    occupation: patient.occupation || "",
    employerName: patient.employerName || "",
    economicStatus: patient.economicStatus || "",
    educationLevel: patient.educationLevel || "",
    tobacco: patient.tobacco || "",
    alcohol: patient.alcohol || "",
    caffeine: patient.caffeine || "",
    cannabis: patient.cannabis || "",
    maritalStatus: patient.maritalStatus || "",
    sexualOrientation: patient.sexualOrientation || "",
    exerciseFrequency: patient.exerciseFrequency || "",
    dietaryRestrictions: patient.dietaryRestrictions || "",
    psychedelics: patient.psychedelics || "",
    hardDrugs: patient.hardDrugs || "",
    military: patient.military || "",
    disability: patient.disability || "",
    healthCardNo: getNestedValue(patient, "healthCard.healthCardNo"),
    healthCardVerCode: getNestedValue(patient, "healthCard.healthCardVerCode"),
    healthCardExpiry: formatPrefillDate(
      getNestedValue(patient, "healthCard.healthCardExpiry")
    ),
    healthCardState: getNestedValue(patient, "healthCard.healthCardState"),
    pid,
    fileNumber,
    patientIdentifier,
  };
}

const PREFILL_MATCHERS = [
  {
    field: "fullName",
    aliases: ["name", "full name", "patient name", "client name", "legal name"],
  },
  {field: "fName", aliases: ["first name", "patient first name", "given name"]},
  {
    field: "lName",
    aliases: ["last name", "patient last name", "surname", "family name"],
  },
  {field: "mName", aliases: ["middle name"]},
  {field: "preferredName", aliases: ["preferred name", "nickname"]},
  {field: "email", aliases: ["email", "email address", "e mail"]},
  {
    field: "phone",
    aliases: ["phone", "phone number", "cell phone", "mobile phone", "mobile"],
  },
  {field: "homePhone", aliases: ["home phone", "home telephone"]},
  {
    field: "alternativePhone",
    aliases: ["alternate phone", "alternative phone", "other phone"],
  },
  {
    field: "dob",
    aliases: [
      "date of birth",
      "birth date",
      "dob",
      "birthday",
      "patient date of birth",
      "client date of birth",
    ],
  },
  {
    field: "birthYear",
    aliases: ["birth year", "year of birth", "date of birth year", "dob year"],
  },
  {
    field: "birthMonth",
    aliases: [
      "birth month",
      "month of birth",
      "date of birth month",
      "dob month",
    ],
  },
  {
    field: "birthDay",
    aliases: ["birth day", "day of birth", "date of birth day", "dob day"],
  },
  {field: "age", aliases: ["age", "patient age", "client age"]},
  {field: "gender", aliases: ["gender", "sex", "sex at birth"]},
  {field: "preferredPronoun", aliases: ["preferred pronoun", "pronouns"]},
  {
    field: "referralVia",
    aliases: ["referral via", "referred by", "referral source"],
  },
  {
    field: "address1",
    aliases: ["address", "address 1", "address line 1", "street address"],
  },
  {
    field: "address2",
    aliases: [
      "address 2",
      "address line 2",
      "unit",
      "suite",
      "apartment",
      "apt",
    ],
  },
  {field: "city", aliases: ["city", "town"]},
  {field: "province", aliases: ["province", "state", "province state"]},
  {
    field: "postalCode",
    aliases: ["postal code", "zip code", "postcode", "zip"],
  },
  {field: "country", aliases: ["country"]},
  {
    field: "emergencyContactName",
    aliases: ["emergency contact", "emergency contact name", "emergency name"],
  },
  {
    field: "emergencyContactEmail",
    aliases: ["emergency contact email", "emergency email"],
  },
  {
    field: "emergencyContactPhone",
    aliases: ["emergency contact phone", "emergency phone"],
  },
  {
    field: "emergencyContactRelation",
    aliases: ["emergency contact relation", "relationship"],
  },
  {field: "height", aliases: ["height", "patient height"]},
  {field: "weight", aliases: ["weight", "patient weight"]},
  {field: "occupation", aliases: ["occupation", "patient occupation"]},
  {field: "employerName", aliases: ["employer", "employer name"]},
  {field: "economicStatus", aliases: ["economic status", "employment status"]},
  {field: "educationLevel", aliases: ["education level", "highest education"]},
  {field: "tobacco", aliases: ["tobacco", "tobacco usage"]},
  {field: "alcohol", aliases: ["alcohol", "alcohol usage"]},
  {field: "caffeine", aliases: ["caffeine", "caffeine usage"]},
  {field: "cannabis", aliases: ["cannabis", "cannabis usage"]},
  {field: "maritalStatus", aliases: ["marital status"]},
  {field: "sexualOrientation", aliases: ["sexual orientation"]},
  {field: "exerciseFrequency", aliases: ["exercise frequency"]},
  {field: "dietaryRestrictions", aliases: ["dietary restrictions"]},
  {
    field: "psychedelics",
    aliases: ["psychedelics", "psychedelics consumption"],
  },
  {field: "hardDrugs", aliases: ["illicit drug consumption", "hard drugs"]},
  {field: "military", aliases: ["military service", "military"]},
  {field: "disability", aliases: ["disability", "impairment", "impairments"]},
  {field: "healthCardNo", aliases: ["health card number", "health card no"]},
  {
    field: "healthCardVerCode",
    aliases: ["health card version code", "health card ver code"],
  },
  {
    field: "healthCardExpiry",
    aliases: ["health card expiry", "health card expiration"],
  },
  {
    field: "healthCardState",
    aliases: ["health card state", "health card province"],
  },
  {
    field: "patientIdentifier",
    aliases: [
      "patient id",
      "patient identifier",
      "client id",
      "client identifier",
      "client number",
      "patient number",
      "member id",
    ],
  },
  {
    field: "fileNumber",
    aliases: ["file number", "chart number", "record number"],
  },
  {field: "pid", aliases: ["pid", "internal id", "internal patient id"]},
];

function resolvePrefillField(labelText, prefillValues) {
  const normalizedLabel = normalizePrefillLabel(labelText);
  if (!normalizedLabel) return null;

  const reservedNameFields = [
    {field: "preferredName", aliases: ["preferred name", "nickname"]},
    {
      field: "fName",
      aliases: ["first name", "patient first name", "given name"],
    },
    {
      field: "lName",
      aliases: ["last name", "patient last name", "surname", "family name"],
    },
    {field: "mName", aliases: ["middle name"]},
  ];

  for (const matcher of reservedNameFields) {
    if (matcher.aliases.some((alias) => normalizedLabel.includes(alias))) {
      return hasMeaningfulValue(prefillValues[matcher.field])
        ? matcher.field
        : null;
    }
  }

  for (const matcher of PREFILL_MATCHERS) {
    if (!hasMeaningfulValue(prefillValues[matcher.field])) continue;
    if (matcher.aliases.some((alias) => normalizedLabel === alias)) {
      return matcher.field;
    }
  }

  for (const matcher of PREFILL_MATCHERS) {
    if (!hasMeaningfulValue(prefillValues[matcher.field])) continue;
    if (
      matcher.aliases.some((alias) => {
        if (alias === "name") return normalizedLabel === alias;
        return normalizedLabel.includes(alias);
      })
    ) {
      return matcher.field;
    }
  }

  return null;
}

function resolvePrefillValueFromDbField(dbField, prefillValues) {
  if (!dbField) return null;

  if (
    Object.prototype.hasOwnProperty.call(prefillValues, dbField) &&
    hasMeaningfulValue(prefillValues[dbField])
  ) {
    return prefillValues[dbField];
  }

  const terminalField = String(dbField).split(".").pop();
  if (
    terminalField &&
    Object.prototype.hasOwnProperty.call(prefillValues, terminalField) &&
    hasMeaningfulValue(prefillValues[terminalField])
  ) {
    return prefillValues[terminalField];
  }

  if (dbField === "fileNumber" || dbField === "pid") {
    return (
      prefillValues.patientIdentifier ||
      prefillValues.fileNumber ||
      prefillValues.pid ||
      null
    );
  }

  const normalizedField = normalizePrefillLabel(dbField).replace(/\s+/g, "");
  const fieldAliases = {
    dateofbirth: "dob",
    birthdate: "dob",
    patientage: "age",
    healthcardhealthcardno: "healthCardNo",
    healthcardhealthcardvercode: "healthCardVerCode",
    healthcardhealthcardexpiry: "healthCardExpiry",
    healthcardhealthcardstate: "healthCardState",
  };

  const aliasField = fieldAliases[normalizedField];
  if (
    aliasField &&
    Object.prototype.hasOwnProperty.call(prefillValues, aliasField) &&
    hasMeaningfulValue(prefillValues[aliasField])
  ) {
    return prefillValues[aliasField];
  }

  return null;
}

function findPrefillValueFromDbData(node, prefillValues) {
  if (!node || typeof node !== "object") return null;

  if (node.dbData?.dbSource === "patients" && node.dbData?.dbField) {
    const directValue = resolvePrefillValueFromDbField(
      node.dbData.dbField,
      prefillValues
    );
    if (hasMeaningfulValue(directValue)) {
      return directValue;
    }
  }

  if (Array.isArray(node.children)) {
    for (const child of node.children) {
      const childValue = findPrefillValueFromDbData(child, prefillValues);
      if (hasMeaningfulValue(childValue)) {
        return childValue;
      }
    }
  }

  return null;
}

function containsFillableControl(node) {
  if (!node || typeof node !== "object") return false;
  if (["input", "textarea", "select"].includes(node.element)) return true;
  if (!Array.isArray(node.children)) return false;
  return node.children.some(containsFillableControl);
}

function replacePatientPlaceholderText(value, prefillValues) {
  if (typeof value !== "string") return value;

  let nextValue = value;
  if (hasMeaningfulValue(prefillValues.fullName)) {
    nextValue = nextValue.replace(
      /\b(Client|Patient)\s+Name\b/g,
      prefillValues.fullName
    );
  }

  if (hasMeaningfulValue(prefillValues.patientIdentifier)) {
    nextValue = nextValue
      .replace(
        /\b(Client|Patient)\s+(ID|Identifier|Number)\b/g,
        prefillValues.patientIdentifier
      )
      .replace(/\b(Client|Patient)\s+No\.\b/g, prefillValues.patientIdentifier);
  }

  return nextValue;
}

function applyStaticPatientPlaceholders(node, prefillValues) {
  if (!node || typeof node !== "object" || containsFillableControl(node))
    return;

  if (typeof node.children === "string") {
    node.children = replacePatientPlaceholderText(node.children, prefillValues);
    return;
  }

  if (Array.isArray(node.children)) {
    node.children.forEach((child) => {
      if (typeof child === "string") return;
      applyStaticPatientPlaceholders(child, prefillValues);
    });
  }
}

export function applyPrefillValueToNode(node, value) {
  if (!node || typeof node !== "object") return;

  const props = node.props || (node.props = {});
  if (node.element === "input") {
    if (props.type === "radio") {
      props.checked =
        normalizePrefillLabel(props.value) === normalizePrefillLabel(value);
    } else if (props.type !== "checkbox" && props.type !== "file") {
      props.value = props.type === "date" ? formatPrefillDate(value) : value;
      delete props.defaultValue;
    }
  } else if (node.element === "textarea" || node.element === "select") {
    props.value = value;
    delete props.defaultValue;
  }

  if (Array.isArray(node.children)) {
    node.children.forEach((child) => applyPrefillValueToNode(child, value));
  }
}

export function prefillSectionsWithPatientData(sections, patient, fallbackPid) {
  if (!Array.isArray(sections) || !patient) return sections;

  const prefillValues = buildPatientPrefillValues(patient, fallbackPid);
  const clonedSections = JSON.parse(JSON.stringify(sections));

  clonedSections.forEach((section) => {
    if (!Array.isArray(section?.fields)) return;
    section.fields.forEach((row) => {
      if (!Array.isArray(row)) return;
      row.forEach((fieldNode) => {
        sanitizePaymentField(fieldNode, section.name);

        if (isSensitivePaymentField(fieldNode, section.name)) {
          return;
        }

        applyStaticPatientPlaceholders(fieldNode, prefillValues);

        if (fieldHasValue(fieldNode)) {
          return;
        }

        const dbPrefillValue = findPrefillValueFromDbData(
          fieldNode,
          prefillValues
        );
        if (hasMeaningfulValue(dbPrefillValue)) {
          applyPrefillValueToNode(fieldNode, dbPrefillValue);
          return;
        }

        const matchedField = resolvePrefillField(
          getFieldLabelText(fieldNode),
          prefillValues
        );
        if (!matchedField) {
          return;
        }

        applyPrefillValueToNode(fieldNode, prefillValues[matchedField]);
      });
    });
  });

  return clonedSections;
}
